<template>
  <LayoutAutenticado>
    <div class="dashboard-container fade-in-up">
      <!-- Header -->
      <header class="dashboard-header">
        <div>
          <h1 class="page-title">Visão Geral</h1>
          <p class="page-subtitle">Acompanhe o desempenho e as atualizações da sua empresa.</p>
        </div>
        <div class="header-actions d-none d-sm-flex">
          <button class="btn-primary" @click="goToPendingServices" v-if="hasPendingServices">
            <i class="bi bi-exclamation-triangle"></i>
            Tratar Pendências
          </button>
        </div>
      </header>

      <!-- Pendências / Alert Banner -->
      <div v-if="hasPendingServices" class="modern-alert alert-warning">
        <div class="alert-icon">
          <i class="bi bi-shield-exclamation"></i>
        </div>
        <div class="alert-content">
          <strong>Atenção Necessária!</strong>
          <p>Você tem <b>{{ pendingServicesCount }}</b> serviço(s) aguardando correção ou análise.</p>
        </div>
        <button class="alert-action" @click="goToPendingServices">Resolver agora</button>
      </div>

      <div v-else class="modern-alert alert-success">
        <div class="alert-icon">
          <i class="bi bi-check-circle-fill"></i>
        </div>
        <div class="alert-content">
          <strong>Tudo em dia!</strong>
          <p>Nenhuma pendência crítica foi encontrada no sistema no momento.</p>
        </div>
      </div>

      <!-- KPIs Grid -->
      <section class="kpi-grid mt-4">
        <div class="kpi-card" v-for="stat in dashboardStats" :key="stat.label">
          <div class="kpi-icon-wrapper">
             <!-- V2 icon mapping from original emoji keys -->
             <i v-if="stat.icon === '🔧'" class="bi bi-tools text-primary"></i>
             <i v-else-if="stat.icon === '✔️'" class="bi bi-check2-all text-success"></i>
             <i v-else-if="stat.icon === '🚚'" class="bi bi-truck text-info"></i>
             <i v-else-if="stat.icon === '🛡️'" class="bi bi-shield-check text-warning"></i>
             <i v-else-if="stat.icon === '❌'" class="bi bi-x-octagon text-danger"></i>
             <i v-else class="bi bi-bar-chart text-secondary"></i>
          </div>
          <div class="kpi-data">
            <h3 class="kpi-value">{{ stat.value }}</h3>
            <span class="kpi-label">{{ stat.label }}</span>
          </div>
        </div>
      </section>

      <!-- Bottom Layout Grid: Notifications vs Empty Charts area -->
      <div class="dashboard-lower mt-4">
        <!-- Notifications Card -->
        <section class="notifications-section panel-card">
          <div class="panel-header">
            <h2 class="panel-title">Notificações Recentes</h2>
            <button class="icon-btn"><i class="bi bi-three-dots"></i></button>
          </div>
          
          <div class="panel-body">
            <div v-if="notifications.length > 0" class="timeline">
              <div class="timeline-item" v-for="notification in notifications" :key="notification.id">
                <div class="timeline-marker"></div>
                <div class="timeline-content">
                  <strong class="timeline-title">{{ notification.title }}</strong>
                  <p class="timeline-text">{{ notification.message }}</p>
                </div>
              </div>
            </div>
            
            <div v-else class="empty-state">
              <div class="empty-icon"><i class="bi bi-bell-slash"></i></div>
              <p>Você não possui avisos recentes.</p>
            </div>
          </div>
        </section>

        <!-- Placeholder for future charts -->
        <section class="quick-actions-section panel-card">
          <div class="panel-header">
            <h2 class="panel-title">Ações Rápidas</h2>
          </div>
          <div class="panel-body px-0">
             <div class="actions-grid">
               <button class="action-tile" @click="$router.push('/prestadores')">
                 <div class="tile-icon"><i class="bi bi-people"></i></div>
                 <span>Novo Prestador</span>
               </button>
               <button class="action-tile" @click="$router.push('/equipamentos')">
                 <div class="tile-icon"><i class="bi bi-motherboard"></i></div>
                 <span>Eqpto. Manual</span>
               </button>
               <button class="action-tile" @click="$router.push('/servicos')">
                 <div class="tile-icon"><i class="bi bi-card-checklist"></i></div>
                 <span>Ordens de Serviço</span>
               </button>
             </div>
          </div>
        </section>
      </div>
    </div>

    <!-- Bootstrap Toast -->
    <div class="toast-container position-fixed bottom-0 end-0 p-3">
      <div id="liveToast" class="toast" role="alert" aria-live="assertive" aria-atomic="true">
        <div class="toast-header">
          <i class="bi bi-info-circle text-primary me-2"></i>
          <strong class="me-auto">Atualização</strong>
          <button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close"></button>
        </div>
        <div class="toast-body" id="toastBody">
          Aqui aparecerá a mensagem.
        </div>
      </div>
    </div>
  </LayoutAutenticado>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import LayoutAutenticado from '../components/layouts/LayoutAutenticado.vue';
import axios from '@/axios.js';
import { showToast } from '@/eventBus.js';

const router = useRouter();

// --- Data ---
const dashboardStats = ref([]);
const pendingServicesCount = ref(0);
const notifications = ref([]);

// --- Computed Properties ---
const hasPendingServices = computed(() => pendingServicesCount.value > 0);

// --- API Calls ---
const loadDashboardData = () => {
  const companyId = localStorage.getItem("idCompany");
  if(!companyId) return;

  axios.get(`/api/home/notifications/${companyId}`)
    .then((response) => {
      notifications.value = response.data || [];
    })
    .catch((error) => {
      showToast(error?.response?.data?.message || 'Erro ao carregar notificações');
    });

  // KPIs
  axios.get(`/api/home/kpis/${companyId}`)
    .then((response) => {
      // Map API to our stat objects preserving icon mapping logic
      dashboardStats.value = [
        { icon: '🔧', value: response.data.totalServicos || 0, label: 'Total de Serviços' },
        { icon: '✔️', value: response.data.totalConcluido || 0, label: 'Total Concluídos' },
        { icon: '🚚', value: response.data.totalVisitas || 0, label: 'Bates de Visitas' },
        { icon: '🛡️', value: response.data.totalGarantias || 0, label: 'Em Garantia' },
        { icon: '❌', value: response.data.totalCancelados || 0, label: 'Cancelados' },
      ];
    })
    .catch((error) => {
      showToast(error?.response?.data?.message || 'Erro ao carregar KPIs');
    });

  // Pending
  axios.get(`/api/home/services/${companyId}`)
    .then((response) => {
      pendingServicesCount.value = response.data;
    })
    .catch((error) => {
      showToast(error?.response?.data?.message || 'Erro ao checar pendências');
    });
};

onMounted(() => {
  loadDashboardData();
});

const goToPendingServices = () => {
  router.push({ path: '/servicos' });
};
</script>

<style scoped>
.dashboard-container {
  width: 100%;
}

/* Header */
.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 2rem;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 800;
  color: #1E293B;
  margin-bottom: 0.25rem;
}

.page-subtitle {
  font-size: 0.95rem;
  color: #64748B;
  margin: 0;
}

.btn-primary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #DF523A;
  color: #fff;
  border: none;
  padding: 0.6rem 1.25rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(223, 82, 58, 0.2);
}

.btn-primary:hover {
  background-color: #c9402a;
  transform: translateY(-1px);
}

/* Modern Alerts */
.modern-alert {
  display: flex;
  align-items: center;
  padding: 1.25rem;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  gap: 1rem;
}

.alert-warning {
  background-color: #FFFBEB;
  border: 1px solid #FEF3C7;
}

.alert-warning .alert-icon {
  background: #FEF3C7;
  color: #D97706;
}

.alert-success {
  background-color: #F0FDF4;
  border: 1px solid #DCFCE7;
}

.alert-success .alert-icon {
  background: #DCFCE7;
  color: #16A34A;
}

.alert-icon {
  width: 48px;
  height: 48px;
  min-width: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.alert-content {
  flex: 1;
}

.alert-content strong {
  display: block;
  font-size: 1.05rem;
  color: #1E293B;
  margin-bottom: 0.15rem;
}

.alert-content p {
  margin: 0;
  font-size: 0.9rem;
  color: #475569;
}

.alert-action {
  background: #D97706;
  color: white;
  border: none;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;
}

.alert-action:hover {
  background: #B45309;
}

@media (max-width: 576px) {
  .modern-alert {
    flex-direction: column;
    align-items: flex-start;
  }
  .alert-action {
    margin-left: 56px;
  }
}

/* KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.25rem;
}

.kpi-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
  transition: all 0.3s ease;
}

.kpi-card:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05);
  transform: translateY(-2px);
  border-color: #CBD5E1;
}

.kpi-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: #F8FAFC;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

/* Mapping generic bootstrap colors for specific KPIs */
.text-primary { color: #3B82F6 !important; }
.text-success { color: #10B981 !important; }
.text-info { color: #0EA5E9 !important; }
.text-warning { color: #F59E0B !important; }
.text-danger { color: #EF4444 !important; }
.text-secondary { color: #64748B !important; }

.kpi-data {
  flex: 1;
}

.kpi-value {
  font-size: 1.75rem;
  font-weight: 800;
  color: #0F172A;
  margin: 0;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.kpi-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Lower Area Layer */
.dashboard-lower {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1.5rem;
}

@media (max-width: 992px) {
  .dashboard-lower {
    grid-template-columns: 1fr;
  }
}

/* Panel Cards */
.panel-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #F1F5F9;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1E293B;
  margin: 0;
}

.icon-btn {
  background: transparent;
  border: none;
  color: #94A3B8;
  cursor: pointer;
  transition: color 0.2s;
}

.icon-btn:hover { color: #334155; }

.panel-body {
  padding: 1.5rem;
  flex: 1;
}

/* Timeline specific */
.timeline {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.timeline-item {
  display: flex;
  gap: 1rem;
  position: relative;
}

.timeline-item:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 14px;
  left: 5px;
  width: 2px;
  height: calc(100% + 0.5rem);
  background: #E2E8F0;
  z-index: 1;
}

.timeline-marker {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #DF523A;
  border: 2px solid #FFFFFF;
  outline: 2px solid #FEF2F2;
  position: relative;
  z-index: 2;
  margin-top: 3px;
}

.timeline-content {
  flex: 1;
}

.timeline-title {
  display: block;
  font-size: 0.95rem;
  color: #1E293B;
  margin-bottom: 0.25rem;
}

.timeline-text {
  margin: 0;
  font-size: 0.85rem;
  color: #64748B;
  line-height: 1.5;
}

.empty-state {
  text-align: center;
  padding: 2rem 0;
  color: #94A3B8;
}

.empty-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
  opacity: 0.5;
}

/* Actions Grid */
.actions-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
}

.action-tile {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  background: transparent;
  border: none;
  border-bottom: 1px solid #F1F5F9;
  cursor: pointer;
  transition: background 0.2s;
  text-align: left;
}

.action-tile:last-child {
  border-bottom: none;
}

.action-tile:hover {
  background: #F8FAFC;
}

.tile-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #F1F5F9;
  color: #DF523A;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.action-tile span {
  font-weight: 600;
  color: #334155;
  font-size: 0.95rem;
}

/* Animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.fade-in-up {
  animation: fadeInUp 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
}

/* Ensures layout renders properly beneath mobile header */
@media (max-width: 767.98px) {
  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
}
</style>
